/* src/components/ProvideImage.js */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  listImages,
  deleteImage,
  getProxyUrl
} from '../services/imageCrud';
import { getCurrentUserId, getProjectsByUserId } from '../services/database';
import { XCircle, Trash2 } from 'lucide-react';
import S3ImageUpload from './S3ImageUpload';

/* --------------------------------------------------
 * main component
 * -------------------------------------------------- */
const ProvideImage = ({ onClose, onSelect, projectId }) => {
  /* --------------------------------------------------
   * state
   * -------------------------------------------------- */
  const [activeTab, setActiveTab] = useState('choose');
  const [selectedProject, setSelectedProject] = useState(projectId || 'all');
  const [projects, setProjects] = useState([]);

  const [images, setImages] = useState([]);
  const [displayedImages, setDisplayedImages] = useState([]); // Displayed images (subset)
  const [page, setPage] = useState(1); // Current page
  const [hasMore, setHasMore] = useState(false); // Flag for more images
  const [loadingMore, setLoadingMore] = useState(false); // Flag for loading more images
  const [selectedImage, setSelectedImage] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [userId, setUserId] = useState(null);
  
  const PAGE_SIZE = 20; // Number of images per page
  const lastClickTimeRef = useRef(0); // Reference to track last click time

  /* delete overlay countdown { imageId, count } | null */
  const [deleteCountdown, setDeleteCountdown] = useState(null);
  const timerRef = useRef(null);

  /* fade-in on project switch */
  const [fadeIn, setFadeIn] = useState(true);

  /* --------------------------------------------------
   * helpers
   * -------------------------------------------------- */
  /* truncate filenames to 15 chars */
  const truncateName = (name, max = 15) => {
    if (!name) return '';
    return name.length > max ? `${name.slice(0, max - 3)}...` : name;
  };

  /* --------------------------------------------------
   * data loading
   * -------------------------------------------------- */
  const fetchProjects = useCallback(async () => {
    try {
      const uid = await getCurrentUserId();
      setUserId(uid);

      const userProjects = await getProjectsByUserId();
      setProjects([
        { id: 'all', title: 'All Projects' },
        ...userProjects.map((p) => ({ id: p._id, title: p.title || 'Untitled Project' }))
      ]);
    } catch (e) {
      console.error(e);
      setErrorMessage('Could not load projects');
    }
  }, []);

  const fetchImages = useCallback(
    async (projId) => {
      if (!userId) return;
      try {
        setLoading(true);
        // NOTE FOR FUTURE: This could be optimized by adding server-side pagination 
        // to only fetch the metadata for visible images (e.g., ?limit=PAGE_SIZE&offset=0)
        const imgs = await listImages(projId === 'all' ? null : projId, userId);
        setImages(imgs);
        
        // Set the first page of images to display
        setDisplayedImages(imgs.slice(0, PAGE_SIZE));
        setPage(1);
        setHasMore(imgs.length > PAGE_SIZE);
      } catch (e) {
        console.error(e);
        setErrorMessage('Could not load images');
      } finally {
        setLoading(false);
        /* trigger fade-in once images are ready */
        setFadeIn(true);
      }
    },
    [userId, PAGE_SIZE]
  );
  
  // Function to load more images with throttling
  const loadMoreImages = useCallback(() => {
    const now = Date.now();
    
    // Throttle to one click every 3 seconds to stay well under the limit
    if (now - lastClickTimeRef.current < 3000 || loadingMore) {
      return; // Ignore clicks that are too frequent
    }
    
    lastClickTimeRef.current = now;
    setLoadingMore(true);
    
    // Use setTimeout to give a visual indication of loading
    // and ensure we don't trigger too many requests at once
    setTimeout(() => {
      const nextPage = page + 1;
      const startIndex = (nextPage - 1) * PAGE_SIZE;
      const endIndex = startIndex + PAGE_SIZE;
      
      const nextBatch = images.slice(startIndex, endIndex);
      setDisplayedImages(prev => [...prev, ...nextBatch]);
      setPage(nextPage);
      setHasMore(endIndex < images.length);
      setLoadingMore(false);
    }, 300);
  }, [page, images, loadingMore, PAGE_SIZE]);

  /* --------------------------------------------------
   * lifecycle
   * -------------------------------------------------- */
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (userId) {
      /* fade out, then fetch new images */
      setFadeIn(false);
      fetchImages(selectedProject);
    }
  }, [selectedProject, userId, fetchImages]);

  /* --------------------------------------------------
   * handle image selection
   * -------------------------------------------------- */
  // Enhanced image selection handler with improved touch support
  const handleImageSelect = useCallback((img) => {
    setSelectedImage(img);
  }, []);

  /* --------------------------------------------------
   * delete logic
   * -------------------------------------------------- */
  const startDeleteCountdown = (image) => {
    cancelDeleteCountdown();

    const realId = image.id || image._id;
    setDeleteCountdown({ imageId: realId, count: 3 });

    timerRef.current = setInterval(() => {
      setDeleteCountdown((prev) => {
        if (!prev) return null;
        if (prev.count <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          handleDeleteConfirm(prev.imageId);
          return null;
        }
        return { ...prev, count: prev.count - 1 };
      });
    }, 1000);
  };

  const cancelDeleteCountdown = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setDeleteCountdown(null);
  };

  // Handle deleting an image and refill if needed
  const handleDeleteConfirm = async (imageId) => {
    try {
      await deleteImage(imageId);
      
      // Update both images arrays
      const updatedImages = images.filter((img) => (img.id || img._id) !== imageId);
      setImages(updatedImages);
      
      // Update displayed images
      const updatedDisplayed = displayedImages.filter((img) => (img.id || img._id) !== imageId);
      
      // Refill logic - if we have fewer than PAGE_SIZE images displayed but more are available
      const currentlyDisplayed = updatedDisplayed.length;
      if (currentlyDisplayed < PAGE_SIZE && page * PAGE_SIZE < updatedImages.length) {
        // Calculate how many more we need
        const needed = PAGE_SIZE - currentlyDisplayed;
        // Get the next batch that would be in the "load more"
        const nextIndex = page * PAGE_SIZE;
        const refillItems = updatedImages.slice(nextIndex, nextIndex + needed);
        
        // Add the refill items to the displayed images
        setDisplayedImages([...updatedDisplayed, ...refillItems]);
      } else {
        setDisplayedImages(updatedDisplayed);
      }
      
      // Update hasMore flag
      setHasMore(page * PAGE_SIZE < updatedImages.length);
      
      if (selectedImage && (selectedImage.id || selectedImage._id) === imageId) {
        setSelectedImage(null);
      }
    } catch (e) {
      console.error(e);
      setErrorMessage('Failed to delete image');
    }
  };

  /* cleanup */
  useEffect(() => () => cancelDeleteCountdown(), []);

  /* --------------------------------------------------
   * handle image selection and upload
   * -------------------------------------------------- */
  const handleImageUploaded = (image) => {
    setImages((prev) => [image, ...prev]);
    // Also update displayed images if on first page
    if (page === 1) {
      setDisplayedImages(prev => {
        const newDisplayed = [image, ...prev];
        // Keep only PAGE_SIZE images
        return newDisplayed.slice(0, PAGE_SIZE);
      });
    }
    onSelect(image);
    onClose();
  };

  const handleSave = () => {
    if (activeTab === 'choose' && selectedImage) {
      onSelect(selectedImage);
      onClose();
    }
  };

  const handleError = (errorMessage) => {
    setErrorMessage(errorMessage);
  };

  /* --------------------------------------------------
   * derived values
   * -------------------------------------------------- */
  const canSave = activeTab === 'choose' && selectedImage;

  /* --------------------------------------------------
   * render
   * -------------------------------------------------- */
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl h-5/6 flex flex-col">
        {/* header */}
        <div className="flex items-center justify-between border-b p-4 md:p-6">
          <h1 className="text-xl font-bold">Provide Image</h1>
          <button className="text-gray-600 hover:text-black" onClick={onClose}>
            <XCircle size={24} />
          </button>
        </div>

        {/* tabs */}
        <div className="flex space-x-8 px-6 pt-4">
          {['choose', 'upload'].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setErrorMessage('');
                if (tab === 'choose') {
                  setSelectedImage(null);
                }
              }}
              className={`pb-2 text-lg ${
                activeTab === tab
                  ? 'text-black border-b-2 border-black'
                  : 'text-gray-600 border-b-2 border-transparent'
              }`}
            >
              {tab === 'choose' ? 'Choose' : 'Upload'}
            </button>
          ))}
        </div>

        {/* body */}
        <div className="p-6 flex-grow overflow-auto">
          {/* CHOOSE TAB */}
          {activeTab === 'choose' && (
            <>
              {/* project selector */}
              <div className="mb-4">
                {errorMessage && <p className="text-red-600 text-sm mb-2">{errorMessage}</p>}
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="border rounded px-4 py-2 w-64"
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </div>

              {loading ? (
                <p className="text-center">Loading…</p>
              ) : displayedImages.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  No images found
                </div>
              ) : (
                <>
                  <div
                    className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 transition-opacity duration-300 ${
                      fadeIn ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {displayedImages.map((img) => {
                      const imgId = img.id || img._id;
                      const isSelected =
                        selectedImage &&
                        (selectedImage.id || selectedImage._id) === imgId;

                      return (
                        <div
                          key={imgId}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleImageSelect(img)}
                          onTouchStart={() => handleImageSelect(img)}
                          className={`group relative cursor-pointer border-2 p-2 rounded-lg transition ${
                            isSelected
                              ? 'border-blue-600'
                              : 'border-transparent hover:border-blue-600'
                          }`}
                        >
                          {/* thumbnail */}
                          <div className="bg-gray-200 aspect-[4/3] overflow-hidden flex items-center justify-center">
                            <img
                              src={getProxyUrl(imgId, userId)}
                              alt={img.fileName}
                              className="object-cover w-full h-full"
                              onError={(e) => {
                                e.target.src = '';
                                e.target.alt = 'Failed';
                              }}
                            />
                          </div>

                          {/* delete button (appears on hover) */}
                          <button
                            title="Delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              startDeleteCountdown(img);
                            }}
                            className="opacity-0 group-hover:opacity-100 absolute top-2 right-2 bg-gray-400/80 p-1 rounded-full text-white hover:bg-red-600 transition"
                          >
                            <Trash2 size={14} />
                          </button>

                          {/* overlay countdown */}
                          {deleteCountdown && deleteCountdown.imageId === imgId && (
                            <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center z-10">
                              <p className="mb-4 text-lg">Deleting image: {deleteCountdown.count}</p>
                              <button
                                onClick={cancelDeleteCountdown}
                                className="px-4 py-2 bg-gray-300 rounded"
                              >
                                Cancel
                              </button>
                            </div>
                          )}

                          {/* caption */}
                          <p className="truncate text-sm text-center mt-2">
                            {truncateName(img.fileName)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Load more button - now with loading state and throttling */}
                  {hasMore && (
                    <div className="flex justify-center mt-6">
                      <button
                        onClick={loadMoreImages}
                        disabled={loadingMore}
                        className={`px-4 py-2 rounded transition-colors ${
                          loadingMore 
                            ? 'bg-gray-300 cursor-not-allowed' 
                            : 'bg-gray-200 hover:bg-gray-300'
                        }`}
                      >
                        {loadingMore ? 'Loading...' : 'Load More Images'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* UPLOAD TAB */}
          {activeTab === 'upload' && (
            <>
              {errorMessage && <p className="text-red-600 text-sm mb-4">{errorMessage}</p>}
              <S3ImageUpload 
                projectId={projectId} 
                onImageUploaded={handleImageUploaded}
                onError={handleError} 
              />
            </>
          )}
        </div>

        {/* footer */}
        <div className="border-t p-4 flex justify-end space-x-4">
          <button className="px-6 py-2 bg-gray-200 rounded" onClick={onClose}>
            Cancel
          </button>
          {activeTab === 'choose' && (
            <button
              onClick={handleSave}
              disabled={!canSave || loading}
              className={`px-6 py-2 text-white rounded transition-colors ${
                canSave && !loading ? 'bg-black hover:bg-gray-800' : 'bg-black/50 cursor-not-allowed'
              }`}
            >
              {loading ? 'Saving…' : 'Use Image'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProvideImage;